import {
    isQueryParameterPresent,
    timeUtils,
} from '@instinet-analytics/core-lib';
import { MarketDataBarItem, MarketDataRefData } from './store/MarketDataState';
import {
    selectIndigoMarketDataEnabled,
    selectLatestMarketTickData,
    selectMarketBarDataBars,
    selectMarketBarDataMessages,
    selectMarketDataMessages,
    selectRefData,
} from './store/selectors';
import { useAppSelector } from 'store/hooks';
import { useMemo } from 'react';
import State from 'store/State';
import { StreamMessage } from 'utils/messages/StreamMessage';
import { usePortfolioSelection } from 'portfolio/store/hooks';
import { useSymbolInBloombergSymbology } from 'symbols/symbolInfo/hooks';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';

const { roundToDays } = timeUtils;

const useSelectBarDataBars = (
    bbSymbol: string | undefined,
    binSize: number
) => {
    return useAppSelector((state) =>
        bbSymbol ? selectMarketBarDataBars(state, bbSymbol, binSize) : undefined
    );
};

export const useExternalMarketDataRefData = (
    symbol: string | undefined,
    symbology: SymbologyValues
) => {
    const bbSymbol = useSymbolInBloombergSymbology(symbol, symbology);

    return useAppSelector((state) => selectRefData(state, bbSymbol));
};

export const useIndigoMarketDataEnabled = () => {
    const indigoMarketDataEnabled = useAppSelector(
        selectIndigoMarketDataEnabled
    );

    const queryParameterOverride = isQueryParameterPresent('mktdata', [
        '1',
        'true',
    ]);

    if (queryParameterOverride) {
        // Existence of 'mktdata' query parameter forces external market data mode...
        return false;
    }

    return indigoMarketDataEnabled;
};

export function getExternalMarketBarData(
    usingIndigoMarketData: boolean,
    bars?: MarketDataBarItem[],
    refData?: MarketDataRefData
): MarketDataBarItem[] | undefined {
    if (usingIndigoMarketData) {
        return undefined;
    }

    if (!bars) {
        return undefined;
    }
    return bars.filter((b) => {
        if (refData) {
            return (
                b.time >= refData.startTradingHoursTimestamp &&
                b.time <= refData.endTradingHoursTimestamp
            );
        }
        return b.time >= roundToDays(new Date().getTime());
    });
}

export function useExternalMarketBarData(
    symbol: string | undefined,
    symbology: SymbologyValues,
    binSize: number
): MarketDataBarItem[] | undefined {
    const bbSymbol = useSymbolInBloombergSymbology(symbol, symbology);
    const usingIndigoMarketData = useIndigoMarketDataEnabled();
    const bars = useSelectBarDataBars(bbSymbol, binSize);
    const refData = useExternalMarketDataRefData(
        bbSymbol,
        SymbologyValues.Bloomberg
    );

    return useMemo(() => {
        return getExternalMarketBarData(usingIndigoMarketData, bars, refData);
    }, [usingIndigoMarketData, bars, refData]);
}

const NoMessages: StreamMessage[] = [];

export function useExternalMarketBarDataMessages(
    symbol: string | undefined,
    symbology: SymbologyValues,
    binSize: number
): StreamMessage[] {
    const bbSymbol = useSymbolInBloombergSymbology(symbol, symbology);
    const usingIndigoMarketData = useIndigoMarketDataEnabled();
    const marketDataMessages = useAppSelector(selectMarketDataMessages);
    const marketDataBarMessages = useAppSelector((state: State) =>
        bbSymbol
            ? selectMarketBarDataMessages(state, bbSymbol, binSize)
            : NoMessages
    );

    return useMemo(
        () =>
            usingIndigoMarketData
                ? NoMessages
                : [...marketDataMessages, ...(marketDataBarMessages ?? [])],
        [marketDataMessages, marketDataBarMessages, usingIndigoMarketData]
    );
}

export function useSymbolExternalMarketData(
    symbol: string | undefined,
    symbology: SymbologyValues,
    bucketSize: number
) {
    const barData = useExternalMarketBarData(symbol, symbology, bucketSize);
    const refData = useExternalMarketDataRefData(symbol, symbology);
    const messages = useExternalMarketBarDataMessages(
        symbol,
        symbology,
        bucketSize
    );

    return useMemo(() => {
        return { barData, refData, messages };
    }, [barData, refData, messages]);
}

export function useSymbolExternalMarketDataLatestTick(
    symbol: string | undefined,
    symbology: SymbologyValues
) {
    const bbSymbol = useSymbolInBloombergSymbology(symbol, symbology);
    return useAppSelector((s) => selectLatestMarketTickData(s, bbSymbol));
}

export function useSelectedSymbolExternalMarketData(bucketSize: number) {
    const { selectedSymbol, symbology } = usePortfolioSelection();
    return useSymbolExternalMarketData(selectedSymbol, symbology, bucketSize);
}
